import React, { useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AuthImg } from "@/components/AuthImg";
import { toast } from "sonner";
import {
  BookOpen, Mic2, UserRoundCheck, FileText, Plus, Trash2, Upload, Loader2, ExternalLink, FileSearch,
} from "lucide-react";

const TYPE_META = {
  oral_testimony:      { icon: Mic2,          color: "#993D22", key: "sourceOral" },
  documentary:         { icon: BookOpen,      color: "#2C4230", key: "sourceDocumentary" },
  personal_knowledge:  { icon: UserRoundCheck,color: "#6B4226", key: "sourcePersonal" },
  other:               { icon: FileText,      color: "#A96B18", key: "sourceOther" },
};

export const SourcesSection = ({ person, canEdit, onChanged }) => {
  const { API, user } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ type: "oral_testimony", description: "", witness_name: "", document_path: null, date_recorded: "" });
  const fileRef = useRef(null);

  const resetForm = () => setForm({ type: "oral_testimony", description: "", witness_name: "", document_path: null, date_recorded: "" });

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, document_path: data.path }));
      toast.success(t("uploadDocument") + " ✓");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) {
      toast.error(t("sourceDescription") + " " + t("required"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/persons/${person.id}/sources`, form);
      toast.success(data?.pending_review ? "Source envoyée pour validation par un administrateur." : t("sourceAdded"));
      setOpen(false);
      resetForm();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const remove = async (sid) => {
    try {
      await axios.delete(`${API}/persons/${person.id}/sources/${sid}`);
      toast.success(t("sourceDeleted"));
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const sources = person.sources || [];

  return (
    <section className="mb-8" data-testid="sources-section">
      <div className="flex items-end justify-between mb-3">
        <div className="label-uppercase text-[#6E5B4F] flex items-center gap-2">
          <FileSearch className="w-3.5 h-3.5" /> {t("sources")}
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="border-[#993D22] text-[#993D22]" data-testid="add-source-btn">
                <Plus className="w-4 h-4 mr-1" /> {t("addSource")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FAF7F2] max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">{t("addSource")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="source-form">
                <div>
                  <Label>{t("sourceType")} *</Label>
                  <Select value={form.type} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
                    <SelectTrigger data-testid="source-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_META).map(([k, meta]) => (
                        <SelectItem key={k} value={k}>{t(meta.key)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("sourceDescription")} *</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} required data-testid="source-description-input" />
                </div>
                {form.type === "oral_testimony" && (
                  <div>
                    <Label>{t("witnessName")}</Label>
                    <Input value={form.witness_name} onChange={(e) => setForm(p => ({ ...p, witness_name: e.target.value }))} data-testid="witness-name-input" />
                  </div>
                )}
                <div>
                  <Label>{t("sourceDate")}</Label>
                  <Input type="date" value={form.date_recorded} onChange={(e) => setForm(p => ({ ...p, date_recorded: e.target.value }))} data-testid="source-date-input" />
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadDoc} data-testid="source-file-input" />
                  <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-[#6B4226] text-[#6B4226]" data-testid="upload-doc-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {t("uploadDocument")}
                    </Button>
                    {form.document_path && (
                      <span className="label-uppercase text-[#2C4230]">✓ joint</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                  <Button type="submit" className="btn-terra" disabled={saving} data-testid="submit-source-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {t("save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {sources.length === 0 ? (
        <div className="heritage-card p-4 text-sm italic text-[#6E5B4F]">{t("noSources")}</div>
      ) : (
        <ul className="space-y-3">
          {sources.map(s => {
            const meta = TYPE_META[s.type] || TYPE_META.other;
            const Icon = meta.icon;
            const canDelete = canEdit && (user?.role === "elder" || user?.role === "admin" || s.recorded_by === user?.id);
            return (
              <li key={s.id} className="heritage-card p-4" data-testid={`source-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: meta.color }}>
                    <Icon className="w-5 h-5 text-[#FAF7F2]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="label-uppercase" style={{ color: meta.color }}>{t(meta.key)}</span>
                      {s.witness_name && (
                        <span className="text-xs text-[#3A2312]">· {s.witness_name}</span>
                      )}
                      {s.date_recorded && (
                        <span className="text-xs text-[#6E5B4F]">· {s.date_recorded}</span>
                      )}
                    </div>
                    <p className="text-[#261911] text-sm whitespace-pre-wrap">{s.description}</p>
                    {s.document_path && (
                      <div className="mt-2">
                        <AuthImg
                          path={s.document_path}
                          alt="document"
                          className="max-h-40 rounded-md border border-[#E3D5CA]"
                          fallback={
                            <a
                              href={`${API}/files/${s.document_path}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-[#993D22] font-semibold"
                              data-testid={`source-doc-link-${s.id}`}
                            >
                              <FileText className="w-4 h-4" /> {t("uploadDocument")} <ExternalLink className="w-3 h-3" />
                            </a>
                          }
                        />
                      </div>
                    )}
                    <div className="mt-2 label-uppercase text-[#6E5B4F]">
                      {t("recordedBy")} {s.recorded_by_name || "—"} · {new Date(s.recorded_at).toLocaleDateString()}
                    </div>
                  </div>
                  {canDelete && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-red-700 hover:text-red-800"
                      onClick={() => remove(s.id)}
                      data-testid={`remove-source-${s.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default SourcesSection;
